import { useSortable } from "@dnd-kit/sortable";
import { DraggableSyntheticListeners } from "@dnd-kit/core";
import AddIcon from "@mui/icons-material/Add";
import DragIcon from "@mui/icons-material/DragIndicator";
import { Box, BoxProps, IconButton, SxProps, useTheme } from "@mui/material";
import React, { ReactNode } from "react";
import { useMenu } from "../../hook/menu";
import {
  getBlockType,
  NoteBlock,
  NoteBlockType,
} from "../../types/notebook/note/block";
import { HtmlBlock } from "../../types/notebook/note/block/html";
import { AddBlockMenu, BlockActionMenu } from "../menu/page/block";
import { ChartBlockComponent } from "./block/chart";
import { CodeBlockComponent } from "./block/codeBlock";
import { TableBlockComponent } from "./block/codeTable";
import { ComponentBlockComponent } from "./block/component";
import { CronBlockComponent } from "./block/cron";
import { HtmlBlockComponent } from "./block/html";
import { ImplementationBlockImplementation } from "./block/implementation";
import { NotePageBlockComponent } from "./block/page";
import { ProtocolBlockProtocol } from "./block/protocol";
import { NotePageBlockPushConnector } from "./block/pushConnector";
import { LogStreamBlockComponent } from "./block/logStream";
import { HttpHookBlockComponent } from "./block/httpHook";
import { PackageBlockComponent } from "./block/package";
import { SubscriptionBlockComponent } from "./block/subscription";
import { ServiceAccountBlockComponent } from "./block/serviceAccount";
import { ServiceAccountBlock } from "../../types/notebook/note/block/serviceAccount";
import { DevcontainerBlockComponent } from "./block/devcontainer";
import { VolumeBlockComponent } from "./block/volume";
import { WebSocketBlockComponent } from "./block/webSocket";
import { DevcontainerBlock } from "../../types/notebook/note/block/devcontainer";
import { VolumeBlock } from "../../types/notebook/note/block/volume";
import { CronBlock } from "../../types/notebook/note/block/cron";
import { SubscriptionBlock } from "../../types/notebook/note/block/subscription";

export const DropMarker: React.FC<{ active?: boolean }> = ({ active }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        width: "100%",
        height: "2px",
        backgroundColor: active ? theme.palette.primary.main : "transparent",
        borderRadius: "1px",
        margin: "2px 0",
      }}
    />
  );
};

export const BlockButtons: React.FC<{
  blockIndex: number;
  listeners: DraggableSyntheticListeners | undefined;
}> = React.memo(({ blockIndex, listeners }) => {
  const {
    handleOpen: addBlockHandleOpen,
    open: addBlockOpen,
    onClose: addBlockOnClose,
    anchorEl: addBlockAnchorEl,
  } = useMenu();
  const {
    handleOpen: blockActionHandleOpen,
    open: blockActionOpen,
    onClose: blockActionOnClose,
    anchorEl: blockActionAnchorEl,
  } = useMenu();
  const iconProps: SxProps = {
    opacity: "0.25",
    "&:hover": { opacity: "0.75" },
    padding: "0.0rem",
  };

  return (
    <Box
      className="block-buttons"
      sx={{ position: "relative", opacity: 0.18, transition: "opacity 120ms ease" }}
    >
      <Box
        sx={{
          position: "absolute",
          display: "flex",
          flexDirection: "column",
          translate: "-100%",
        }}
      >
        <AddBlockMenu
          onClose={addBlockOnClose}
          open={addBlockOpen}
          anchorEl={addBlockAnchorEl}
          blockIndex={blockIndex}
        />
        <BlockActionMenu
          onClose={blockActionOnClose}
          open={blockActionOpen}
          anchorEl={blockActionAnchorEl}
          blockIndex={blockIndex}
        />
        <IconButton
          data-testid={`add-block-before-${blockIndex}`}
          aria-label={`add-block-before-${blockIndex}`}
          onClick={(e) => addBlockHandleOpen(e)}
          sx={{ ...iconProps }}
        >
          <AddIcon />
        </IconButton>
        <IconButton
          {...listeners}
          onContextMenu={(e) => {
            e.preventDefault();
            blockActionHandleOpen(e);
          }}
          sx={{ ...iconProps }}
        >
          <DragIcon />
        </IconButton>
      </Box>
    </Box>
  );
});

export const BlockWrapper: React.FC<
  {
    children?: ReactNode;
    blockId: string;
    blockIndex: number;
    showDropMarkerAbove?: boolean;
    writeAccess: boolean;
  } & BoxProps
> = React.memo(({ children, blockId, blockIndex, showDropMarkerAbove, writeAccess, ...boxProps }) => {
  const { setNodeRef, listeners } = useSortable({ id: blockId });

  return (
    <>
      <DropMarker active={showDropMarkerAbove} />
      <Box
        data-testid={`page-block-${blockIndex}`}
        ref={setNodeRef}
        sx={{
          paddingBottom: "8px",
          scrollMarginTop: "7rem",
          "&:hover .block-buttons, &:focus-within .block-buttons": { opacity: 1 },
        }}
        {...boxProps}
      >
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "start", gap: "4px" }}>
          {writeAccess && (
            <BlockButtons blockIndex={blockIndex} listeners={listeners} />
          )}
          <Box sx={{ display: "flex", width: "100%", flexDirection: "column" }}>
            {children}
          </Box>
        </Box>
      </Box>
    </>
  );
});

export const BlockElement: React.FC<{
  block: NoteBlock;
  blockIndex: number;
}> = ({ block, blockIndex }) => {
  const blockType = getBlockType(block);
  switch (blockType) {
    case NoteBlockType.HTML:
      return (
        <HtmlBlockComponent
          block={block as HtmlBlock}
          blockIndex={blockIndex}
        />
      );
    case NoteBlockType.TABLE:
      return <TableBlockComponent block={block as any} />;
    case NoteBlockType.CHART:
      return <ChartBlockComponent block={block as any} />;
    case NoteBlockType.CODE:
      return <CodeBlockComponent block={block as any} />;
    case NoteBlockType.PAGE:
      return <NotePageBlockComponent block={block as any} blockIndex={blockIndex} />;
    case NoteBlockType.COMPONENT:
      return <ComponentBlockComponent block={block as any} />;
    case NoteBlockType.PROTOCOL:
      return <ProtocolBlockProtocol block={block as any} />;
    case NoteBlockType.IMPLEMENTATION:
      return <ImplementationBlockImplementation block={block as any} />;
    case NoteBlockType.PUSH_CONNECTOR:
      return <NotePageBlockPushConnector block={block as any} />;
    case NoteBlockType.LOG_STREAM:
      return <LogStreamBlockComponent block={block as any} />;
    case NoteBlockType.PACKAGE:
      return <PackageBlockComponent block={block as any} />;
    case NoteBlockType.SUBSCRIPTION:
      return <SubscriptionBlockComponent block={block as SubscriptionBlock} />;
    case NoteBlockType.CRON:
      return <CronBlockComponent block={block as CronBlock} />;
    case NoteBlockType.HTTP_HOOK:
      return <HttpHookBlockComponent block={block as any} />;
    case NoteBlockType.SERVICE_ACCOUNT:
      return (
        <ServiceAccountBlockComponent block={block as ServiceAccountBlock} />
      );
    case NoteBlockType.DEVCONTAINER:
      return <DevcontainerBlockComponent block={block as DevcontainerBlock} />;
    case NoteBlockType.VOLUME:
      return <VolumeBlockComponent block={block as VolumeBlock} />;
    case NoteBlockType.WEBSOCKET:
      return <WebSocketBlockComponent block={block as any} />;
    default: {
      const _exhaustive: never = blockType;
      return null;
    }
  }
};

export const EndDropZone: React.FC<{ id: string; active: boolean }> = ({ id, active }) => {
  const { setNodeRef } = useSortable({ id });
  return (
    <Box ref={setNodeRef} sx={{ minHeight: "64px", width: "100%" }}>
      <DropMarker active={active} />
    </Box>
  );
};

export const PageBlockComponent: React.FC<
  {
    block: NoteBlock;
    blockId: string;
    blockIndex: number;
    showDropMarkerAbove?: boolean;
    writeAccess: boolean;
  } & BoxProps
> = ({ block, blockId, blockIndex, showDropMarkerAbove, writeAccess, ...boxProps }) => {
  return (
    <BlockWrapper
      blockId={blockId}
      blockIndex={blockIndex}
      showDropMarkerAbove={showDropMarkerAbove}
      writeAccess={writeAccess}
      {...boxProps}
    >
      <BlockElement block={block} blockIndex={blockIndex} />
    </BlockWrapper>
  );
};
